import EhrSessionsListLoader from '@/components/layout/Navigation/sessions/side-nav-list/integration/ehr-sessions-list/ehr-sessions-list-loader';
import { useLinkEhrPatientToSession } from '@/components/layout/Navigation/sessions/side-nav-list/integration/hooks/useLinkEhrPatientToSession';
import { isEHRAppointment } from '@/components/layout/Navigation/sessions/side-nav-list/integration/hooks/useRetrieveAppointmentOrSchedule';
import { UpcomingSessionItem } from '@/features/upcomingSessionTab/components/upcoming-session-item';
import { UpcomingSessionsListLoader } from '@/features/upcomingSessionTab/components/upcoming-sessions-list-loader';
import { useUpcomingSessionsList } from '@/features/upcomingSessionTab/hooks/useUpcomingSessionsList';
import { useMutateStatePostIntegrationWorkflowSession } from '@/hooks/mutation/integration/direct-backend/useMutatePostIntegrationWorkflowSession';
import { useIsMobile } from '@/hooks/useWindowDimensions';
import { cn } from '@/lib/cn';
import { TypographyP2 } from '@heidi/design-system/typography';
import { useTranslations } from '@heidi/runtime/i18n';
import { startTracking, TrackingKey } from '@heidi/runtime/performance';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import { isUpcomingSessionsListLoadingAtom, type UpcomingSession } from '@heidi/session';
import { getTimezone } from '@heidi/utils/get-country';
import { useAtomValue } from 'jotai';
import { CalendarX } from 'lucide-react';
import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';

export const UpcomingSessionsList = () => {
  const isMobile = useIsMobile();
  const currentSessionId = useCurrentSessionId();

  const router = useRouter();
  const { t } = useTranslations();
  const { upcomingSessions } = useUpcomingSessionsList();

  const { isPending: isLinkEhrPatientToSessionPending, onLinkEhrPatientToSession } =
    useLinkEhrPatientToSession();
  const {
    mutateAsync: postIntegrationWorkflowSession,
    isPending: isPostIntegrationWorkflowSessionPending,
  } = useMutateStatePostIntegrationWorkflowSession();
  const isUpcomingSessionsListLoading = useAtomValue(isUpcomingSessionsListLoadingAtom);

  const isPending =
    isLinkEhrPatientToSessionPending || isPostIntegrationWorkflowSessionPending;

  const showNoSessions = useMemo(() => {
    return (
      !isUpcomingSessionsListLoading && upcomingSessions && !upcomingSessions?.length
    );
  }, [upcomingSessions, isUpcomingSessionsListLoading]);

  const handleSessionStart = useCallback(
    (session: UpcomingSession) => {
      if (isPending || session.session_id === currentSessionId) return;

      if (session.isFromEhr && session?.ehrApptData) {
        if (isEHRAppointment(session.ehrApptData)) {
          void onLinkEhrPatientToSession(session.ehrApptData);
        } else {
          void postIntegrationWorkflowSession({
            session: { is_auto_mode: false, timezone: getTimezone() },
            appointment: session.ehrApptData.appointment,
            patient: session.ehrApptData.patient,
          });
        }
      } else if (session.session_id) {
        startTracking({
          key: TrackingKey.SCRIBE_SESSION_OPEN,
          metadata: { session_id: session.session_id },
        });
        void router.push(`/scribe/session/${session.session_id}`);
      }
    },
    [
      currentSessionId,
      isPending,
      onLinkEhrPatientToSession,
      postIntegrationWorkflowSession,
      router,
    ]
  );

  const getSessionKey = useCallback((session: UpcomingSession, index: number) => {
    if (session.session_id) {
      return session.session_id;
    } else if (session.ehrApptData && isEHRAppointment(session.ehrApptData)) {
      return session.ehrApptData.record_id;
    }
    // Fallback to index and created time local and display name if no session id or record id
    return `${index}-${session.created_time_local}-${session.displayName}`;
  }, []);

  if (showNoSessions) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4">
        <div className="content-center rounded-lg bg-background-tertiary p-4">
          <CalendarX size={23} className="text-text-secondary" />
        </div>
        <TypographyP2 className="text-text-secondary">
          {t('sessions.noSessionsFound')}
        </TypographyP2>
      </div>
    );
  }

  return (
    <div className={cn('flex h-full flex-col gap-1 overflow-y-auto', isMobile && 'pb-8')}>
      {upcomingSessions?.map((session, i) => {
        return (
          <UpcomingSessionItem
            key={getSessionKey(session, i)}
            session={session}
            index={i}
            onClick={() => handleSessionStart(session)}
          />
        );
      })}
      {isPending && <EhrSessionsListLoader />}
      {isUpcomingSessionsListLoading && <UpcomingSessionsListLoader />}
    </div>
  );
};
